import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Appearance, loadStripe, StripePaymentElementOptions } from '@stripe/stripe-js';
import { PaymentElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import Button, { LoadingButton } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { N0, N100, N200, R500 } from '@atlaskit/theme/colors';
import { getSetupIntents } from '@atlassian/temp-proxies';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { usePollPaymentMethod } from './hooks/use-poll-payment-method';
import { usePaymentUpdateNotification } from './hooks/use-payment-update-notification';

type StripePaymentElementsProps = {
  txaId: string;
  selectedPaymentMethodType: PaymentMethodTypeV2;
  onUpdateDone?: () => void;
  onUpdateFailure?: () => void;
  onUpdateSuccess?: () => void;
  onCancel?: () => void;
};

export const StripePaymentElements: React.FC<StripePaymentElementsProps> = ({
  txaId,
  selectedPaymentMethodType,
  onUpdateDone,
  onUpdateFailure,
  onUpdateSuccess,
  onCancel,
}) => {
  const [setupIntentLoading, setSetupIntentLoading] = useState(true);
  const [setupIntent, setSetupIntent] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getSetupIntents(txaId);
      setSetupIntent(response);
      setSetupIntentLoading(false);
    };

    fetchData();
  }, [txaId]);

  const providerOptions = useMemo(() => {
    // Fully customizable with appearance API.
    return {
      appearance: {
        theme: 'stripe' as Appearance['theme'],
        variables: {
          borderRadius: token('border.radius.100', '3px'),
          colorBackground: `${N0}`,
        },
        rules: {
          '.Input': {
            border: `1px solid ${N100}`,
            height: '40px',
            fontSize: '14px',
          },
          '.Label': {
            fontSize: '12px',
            fontWeight: '600',
            color: `${N200}`,
          },
          '.Error': {
            fontSize: '12px',
            fontWeight: '400',
            color: `${R500}`,
          },
        },
      },
      clientSecret: setupIntent?.clientSecret as string,
    };
  }, [setupIntent]);

  if (setupIntentLoading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader width="100%" marginTop="20px" />
      </SkeletonLoaderWrapper>
    );
  }

  const stripePromise = loadStripe(setupIntent?.publicKey as string);

  return (
    <Elements stripe={stripePromise} options={providerOptions}>
      <PaymentElements
        selectedPaymentMethodType={selectedPaymentMethodType}
        onUpdateDone={onUpdateDone}
        onUpdateFailure={onUpdateFailure}
        onUpdateSuccess={onUpdateSuccess}
        onCancel={onCancel}
        setupIntent={setupIntent}
        txaId={txaId}
      />
    </Elements>
  );
};

const CTAContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  gap: ${token('space.200', '16px')};
  align-items: center;
  width: 335px;
  padding: ${token('space.250', '20px')} ${token('space.400', '32px')} ${token('space.400', '32px')} !important;
  margin: ${token('space.050', '4px')} !important;
  button {
    flex-grow: 1;
  }
  background-color: ${token('elevation.surface')};
  border-radius: ${token('space.100', '8px')};
`;

type PaymentElementsProps = StripePaymentElementsProps & {
  setupIntent: Record<string, string> | null;
};

const paymentElementOptions = {
  layout: {
    type: 'accordion',
    defaultCollapsed: false,
    spacedAccordionItems: true,
  },
  paymentMethodOrder: ['us_bank_account'],
} as StripePaymentElementOptions;

export const PaymentElements: React.FC<PaymentElementsProps> = ({
  selectedPaymentMethodType,
  onUpdateDone,
  onUpdateFailure,
  onUpdateSuccess,
  setupIntent,
  txaId,
  onCancel,
}) => {
  const [loading, setLoading] = useState(true);
  const [isCreatingStripePaymentMethod, setIsCreatingStripePaymentMethod] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const { pollPaymentMethod } = usePollPaymentMethod();
  const { notifySuccess, notifyFailure } = usePaymentUpdateNotification('add');

  const createStripePaymentMethod = useCallback(async () => {
    if (!stripe || !elements || !setupIntent?.clientSecret) {
      const errorMessage = 'Stripe not initialized or client secret missing';

      notifyFailure?.(errorMessage);
      return;
    }

    setIsCreatingStripePaymentMethod(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsCreatingStripePaymentMethod(false);
      return;
    }

    try {
      // confirms the SetupIntent with the entered payment details and creates a payment method on stripe
      const { error: confirmSetupError } = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntent.clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (confirmSetupError) {
        const errorMessage = confirmSetupError.message || 'An unexpected error occurred.';
        notifyFailure?.(`${errorMessage}. Try again or contact Partner service desk.`);
        setIsCreatingStripePaymentMethod(false);
        return;
      }

      // once payment method is created on stripe, it pushes a webhook on ccp using which payment method is created on ccp
      // hence we are polling the payment method from ccp until it is created
      const { ccpPaymentMethodId } = await pollPaymentMethod({
        txaId,
        paymentMethodId: setupIntent?.paymentMethodId,
      }).then((response: any) => ({
        ccpPaymentMethodId: response?.id,
      }));

      if (ccpPaymentMethodId) {
        onUpdateSuccess?.();
        onUpdateDone?.();
        notifySuccess?.();
        setIsCreatingStripePaymentMethod(false);
        return {
          data: {
            ccpPaymentMethodId,
          },
        };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred.';
      notifyFailure?.(`${errorMessage}. Try again or contact Partner service desk.`);
      onUpdateFailure?.();
      setIsCreatingStripePaymentMethod(false);
      return;
    }
  }, [
    elements,
    setupIntent,
    stripe,
    pollPaymentMethod,
    onUpdateDone,
    onUpdateSuccess,
    notifySuccess,
    txaId,
    notifyFailure,
    onUpdateFailure,
  ]);

  return (
    <>
      {loading && (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="100%" />
        </SkeletonLoaderWrapper>
      )}
      <PaymentElement options={paymentElementOptions} onLoaderStart={() => setLoading(false)} />
      {selectedPaymentMethodType === PaymentMethodTypeV2.ACH && (
        <CTAContainer>
          <Button testId="ppc.payment-methods-add-ach.cancel" onClick={onCancel}>
            Cancel
          </Button>
          <LoadingButton
            testId="ppc.payment-methods-add-ach.confirm"
            appearance="primary"
            onClick={createStripePaymentMethod}
            isDisabled={isCreatingStripePaymentMethod}
            isLoading={isCreatingStripePaymentMethod}
          >
            Add
          </LoadingButton>
        </CTAContainer>
      )}
    </>
  );
};
